/**
 * Hình học caption — tầng THUẦN giữa config và nét vẽ. Không chạm ctx, không đo
 * chữ: caller đo rồi truyền số vào. Tách khỏi caption.ts vì hai lý do — test
 * được không cần canvas, và frontend gọi lại đúng các hàm này để hiện bề rộng
 * hiệu dụng thay vì tự tính lần hai (tài liệu Phụ lục §4.1 yêu cầu hiện số đó).
 *
 * Hệ quy chiếu: khung `W × H`, trừ lề an toàn ra được BÁT CHỮ `Wb × Hb`. Mọi
 * `position` và `blockWidth` tính trong bát chữ, không tính trong khung.
 *
 * Nguồn: Phụ lục "Mô tả bố cục vị trí và bố cục văn bản cho phụ đề" §3-§6.
 */

/** Lớp khung quyết định bảng lề an toàn và bề rộng khối mặc định. */
export type FrameClass = "landscape" | "portrait" | "square";

/** Vuông trong dung sai này — 1080×1080 và 1000×1002 cùng là "square". */
const SQUARE_TOLERANCE = 0.02;

/** Cạnh ngắn của hệ quy chiếu: mọi số đo không đơn vị trong config đo ở đây. */
const REFERENCE_SHORT_EDGE = 1080;

/** Lề an toàn theo tỷ lệ khung (§5.1, theo SMPTE RP 218 + EBU R95). Khung dọc
 *  chừa nhiều hơn vì short-form phủ UI lên đáy/đỉnh; mọi khung `H > W` tính là
 *  dọc, gồm 4:5 và 3:4 — bảng chỉ liệt kê 9:16 nhưng lý do áp cho cả hai. */
const MARGIN_RATIOS: Record<FrameClass, { x: number; top: number; bottom: number }> = {
  landscape: { x: 0.05, top: 0.05, bottom: 0.05 },
  portrait: { x: 0.08, top: 0.1, bottom: 0.15 },
  square: { x: 0.06, top: 0.06, bottom: 0.06 },
};

/** Bề rộng khối mặc định khi config không đặt (§5.2). */
const DEFAULT_BLOCK_WIDTH: Record<FrameClass, number> = {
  landscape: 80,
  portrait: 90,
  square: 85,
};

/** Bước nhảy dòng = cỡ chữ × hệ số này. Hằng số calibration, giữ nguyên giá trị
 *  của `measureAndWrapText`; wrap.ts và caption.ts phải dùng chung một con số. */
export const LINE_HEIGHT_FACTOR = 1.35;

export interface SafeMargins {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

/** Bát chữ: kích thước cộng gốc toạ độ trong khung. */
export interface TypeArea {
  width: number;
  height: number;
  left: number;
  top: number;
}

export function classifyFrame(canvasWidth: number, canvasHeight: number): FrameClass {
  if (canvasHeight <= 0) return "landscape";
  const ratio = canvasWidth / canvasHeight;
  if (Math.abs(ratio - 1) < SQUARE_TOLERANCE) return "square";
  return ratio > 1 ? "landscape" : "portrait";
}

/** Hệ số nhân cho mọi số đo không đơn vị (cỡ chữ, viền, offset bóng, blur). */
export const captionScale = (canvasWidth: number, canvasHeight: number): number =>
  Math.min(canvasWidth, canvasHeight) / REFERENCE_SHORT_EDGE;

export function safeMargins(canvasWidth: number, canvasHeight: number): SafeMargins {
  const m = MARGIN_RATIOS[classifyFrame(canvasWidth, canvasHeight)];
  return {
    left: canvasWidth * m.x,
    right: canvasWidth * m.x,
    top: canvasHeight * m.top,
    bottom: canvasHeight * m.bottom,
  };
}

export function typeArea(canvasWidth: number, canvasHeight: number): TypeArea {
  const m = safeMargins(canvasWidth, canvasHeight);
  return {
    width: canvasWidth - m.left - m.right,
    height: canvasHeight - m.top - m.bottom,
    left: m.left,
    top: m.top,
  };
}

export const defaultBlockWidth = (frameClass: FrameClass): number =>
  DEFAULT_BLOCK_WIDTH[frameClass];

export interface ResolvedBlockWidth {
  /** Bề rộng khối, px trên khung. Luôn `≤ typeArea.width`. */
  width: number;
  /** Bề rộng hiệu dụng theo % bát chữ — số giao diện hiện cho người dùng. */
  percent: number;
  /** Sàn động đã nới khối ra để chứa từ dài nhất, nên `percent` khác giá trị
   *  người dùng đặt. Giao diện phải hiện cả hai khi cờ này bật (§4.1). */
  floored: boolean;
}

/** Dung sai so sánh float khi xét sàn động có kích hoạt hay không. */
const WIDTH_EPSILON = 0.5;

/**
 * `w = min(max(bw × Wb, từ_dài_nhất), Wb)` (§4.1). Bất biến `w ≤ Wb` giữ khoảng
 * chạy X không âm, nên khối không bao giờ tràn lề ở bất kỳ `position.x` nào. Từ
 * dài hơn cả `Wb` thì khối chốt ở `Wb` và caller phải ngắt giữa ký tự.
 */
export function resolveBlockWidth(
  blockWidth: number | null,
  area: TypeArea,
  longestWordWidth: number,
  frameClass: FrameClass,
): ResolvedBlockWidth {
  const requested = blockWidth ?? defaultBlockWidth(frameClass);
  const wanted = (requested / 100) * area.width;
  const floor = Math.min(Math.max(longestWordWidth, 0), area.width);
  const width = Math.min(Math.max(wanted, floor), area.width);
  return {
    width,
    percent: area.width > 0 ? (width / area.width) * 100 : 0,
    floored: width > wanted + WIDTH_EPSILON,
  };
}

/**
 * Neo tỉ lệ (§4.2): điểm ở `p`% của khối trùng điểm ở `p`% của bát chữ.
 *
 * Kích thước truyền vào là HỘP CHỮ, không gồm độ giãn nền (§4.2 hệ quả 1). `y`
 * không bị kẹp: khối cao hơn bát chữ thì `(Hb − h)` âm và tràn theo `p_y` (§4.3).
 */
export function positionBlock(
  position: { x: number; y: number },
  blockWidth: number,
  blockHeight: number,
  area: TypeArea,
): { x: number; y: number } {
  return {
    x: area.left + (position.x / 100) * (area.width - blockWidth),
    y: area.top + (position.y / 100) * (area.height - blockHeight),
  };
}

/**
 * Trần dòng của `ALL_TEXT`: vừa bao nhiêu dòng trong bát chữ thì vẽ, dòng tràn bỏ
 * (BR_2). Sàn 1 dòng là bắt buộc — cỡ chữ lớn cho `lineHeight > Hb` và `floor()`
 * sẽ ra 0. `BY_SPEECH` dùng `layout.maxLines` thay cho hàm này.
 */
export const maxVisibleLines = (areaHeight: number, lineHeight: number): number =>
  lineHeight > 0 ? Math.max(1, Math.floor(areaHeight / lineHeight)) : 1;
